import { create } from 'zustand';
import type { IUser } from '@tambola/shared';
import { authService } from '../services/auth.service.js';

interface AuthState {
  user: IUser | null;
  isLoading: boolean;
  isSessionChecked: boolean; // has the initial check completed
  error: string | null;
  
  guestLogin: (displayName: string) => Promise<void>;
  googleLogin: (token: string) => Promise<void>;
  facebookLogin: () => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  addCoins: (amount: number) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isSessionChecked: false,
  error: null,

  guestLogin: async (displayName) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.loginAsGuest(displayName);
      set({ user, isLoading: false, isSessionChecked: true });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Guest login failed';
      set({ error: errMsg, isLoading: false });
    }
  },

  googleLogin: async (token) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.loginWithGoogle(token);
      set({ user, isLoading: false, isSessionChecked: true });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Google login failed';
      set({ error: errMsg, isLoading: false, isSessionChecked: true });
    }
  },

  facebookLogin: async () => {
    set({ isLoading: true, error: null });
    try {
      set({ isLoading: false });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Facebook login failed';
      set({ error: errMsg, isLoading: false });
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await authService.logout();
      set({ user: null, isLoading: false, isSessionChecked: false });
    } catch {
      set({ error: 'Logout failed', isLoading: false });
    }
  },

  checkSession: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.getCurrentUser();
      set({ user, isLoading: false, isSessionChecked: true });
    } catch {
      set({ user: null, isLoading: false, isSessionChecked: true });
    }
  },

  addCoins: (amount) => {
    set((state) => {
      if (!state.user) return {};
      return {
        user: {
          ...state.user,
          coins: state.user.coins + amount,
        },
      };
    });
  },
}));
